/**
 * PullConfirmDialogComponent
 *
 * Pre-pull confirmation: shows the four summary fields (cells, source, requests,
 * estimated budget) for the painted grid cells, lets the user pick the source and
 * (for Google) one of their stored API keys, blocks oversized selections, and
 * returns the chosen source + key to the caller. It does not start the pull
 * itself — the sidebar owns the job + progress bar.
 */
import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ApiKeyOption, PlacePullService } from '../../services/place-pull.service';

export interface PullDialogData { cellCount: number; }
export interface PullDialogResult { source: 'osm' | 'google'; apiKeyId?: number; }

@Component({
  selector: 'app-pull-confirm-dialog',
  templateUrl: './pull-confirm-dialog.component.html',
  styleUrls: ['./pull-confirm-dialog.component.css']
})
export class PullConfirmDialogComponent implements OnInit {
  source: 'osm' | 'google' = 'osm';
  apiKeyId?: number;
  apiKeys: ApiKeyOption[] = [];

  // The default self-metered Google unit price, for the pre-pull estimate only.
  // The authoritative running cost comes from the job status while it runs.
  readonly googleUnitCostUsd = 0.032;

  constructor(
    public dialogRef: MatDialogRef<PullConfirmDialogComponent, PullDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: PullDialogData,
    private pull: PlacePullService
  ) { }

  ngOnInit(): void {
    this.pull.getApiKeys().subscribe(keys => this.apiKeys = keys);
  }

  get cellCount(): number { return this.data.cellCount; }
  get requestCount(): number { return this.cellCount; } // one cell = one request
  get budget(): number { return this.source === 'google' ? this.cellCount * this.googleUnitCostUsd : 0; }

  get canConfirm(): boolean {
    return this.cellCount > 0 && (this.source !== 'google' || this.apiKeyId != null);
  }

  confirm(): void {
    if (!this.canConfirm) return;
    this.dialogRef.close({
      source: this.source,
      apiKeyId: this.source === 'google' ? this.apiKeyId : undefined,
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
